import { useEffect, useRef, useState } from 'react';
import { groupApi, siteApi } from '../../api';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ExcelUploadMenu from '../../components/common/ExcelUploadMenu';

const emptyGroup = { name: '', description: '' };
const emptySite = { name: '', groupId: '', latitude: '', longitude: '', address: '' };

export default function GroupSiteManagement() {
  const [groups, setGroups] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('groups');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [deleteBlockedMessage, setDeleteBlockedMessage] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      const [g, s] = await Promise.all([groupApi.list(), siteApi.list()]);
      setGroups(g.data.data);
      setSites(s.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDownloadTemplate = async () => {
    setError('');
    try {
      const res = await siteApi.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sites_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(err.response?.data?.message || 'Failed to download template'); }
  };

  const handleBulkUploadChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBulkLoading(true); setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await siteApi.bulkUpload(formData);
      setBulkResult(res.data.data);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Bulk upload failed'); }
    finally { setBulkLoading(false); }
  };

  const openForm = (isGroup, item = null) => {
    setError('');
    if (isGroup) { setForm(item ? { name: item.name, description: item.description || '' } : emptyGroup); }
    else {
      setForm(item ? {
        name: item.name,
        groupId: item.groupId,
        latitude: item.latitude ?? '',
        longitude: item.longitude ?? '',
        address: item.address || '',
        isActive: item.isActive,
      } : emptySite);
    }
    setEditId(item?.id || null);
    setShowForm(tab);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (showForm === 'groups') {
        editId ? await groupApi.update(editId, form) : await groupApi.create(form);
      } else {
        const data = { ...form, groupId: form.groupId ? parseInt(form.groupId) : null, latitude: form.latitude ? parseFloat(form.latitude) : null, longitude: form.longitude ? parseFloat(form.longitude) : null };
        editId ? await siteApi.update(editId, data) : await siteApi.create(data);
      }
      setShowForm(false);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Error saving'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Groups & Sites" subtitle="Manage site clusters and locations" />

      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>}

      <div className="flex gap-2 mb-6 flex-wrap items-center">
        <button className={tab === 'groups' ? 'btn-primary' : 'btn-secondary'} onClick={() => setTab('groups')}>Groups ({groups.length})</button>
        <button className={tab === 'sites' ? 'btn-primary' : 'btn-secondary'} onClick={() => setTab('sites')}>Sites ({sites.length})</button>
        <input
          className="input max-w-xs"
          placeholder={tab === 'groups' ? 'Search groups…' : 'Search sites…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="ml-auto flex gap-2">
          {tab === 'sites' && (
            <>
              <ExcelUploadMenu
                loading={bulkLoading}
                onDownloadTemplate={handleDownloadTemplate}
                onUploadClick={() => fileInputRef.current?.click()}
              />
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUploadChange} />
            </>
          )}
          <button className="btn-primary" onClick={() => openForm(tab === 'groups')}>
            + Add {tab === 'groups' ? 'Group' : 'Site'}
          </button>
        </div>
      </div>

      {tab === 'groups' && (
        <div className="table-container">
          <table>
            <thead><tr><th>Name</th><th>Description</th><th>Sites</th><th>Actions</th></tr></thead>
            <tbody>
              {groups
                .filter((g) => !search || g.name.toLowerCase().includes(search.toLowerCase()))
                .map((g) => (
                <tr key={g.id}>
                  <td className="font-medium">{g.name}</td>
                  <td className="text-gray-500">{g.description || '—'}</td>
                  <td>{g._count?.sites ?? sites.filter((s) => s.groupId === g.id).length}</td>
                  <td>
                    <div className="flex gap-2">
                      <button className="text-blue-600 hover:underline text-sm" onClick={() => openForm(true, g)}>Edit</button>
                      <button className="text-red-600 hover:underline text-sm" onClick={() => setConfirm({ id: g.id, type: 'group', name: g.name })}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'sites' && (
        <div className="table-container">
          <table>
            <thead><tr><th>Name</th><th>Group</th><th>Address</th><th>Coordinates</th><th>Actions</th></tr></thead>
            <tbody>
              {sites
                .filter((s) => !search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.group?.name || '').toLowerCase().includes(search.toLowerCase()))
                .map((s) => (
                <tr key={s.id}>
                  <td className="font-medium">{s.name}</td>
                  <td>
                    {s.group?.name
                      ? <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">{s.group.name}</span>
                      : <span className="text-gray-400 text-xs">No group</span>}
                  </td>
                  <td className="text-gray-500 max-w-xs truncate">{s.address || '—'}</td>
                  <td className="text-gray-400 text-xs">{s.latitude && s.longitude ? `${s.latitude}, ${s.longitude}` : '—'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button className="text-blue-600 hover:underline text-sm" onClick={() => openForm(false, s)}>Edit</button>
                      <button className="text-red-600 hover:underline text-sm" onClick={() => setConfirm({ id: s.id, type: 'site', name: s.name })}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{editId ? 'Edit' : 'Create'} {showForm === 'groups' ? 'Group' : 'Site'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Name</label>
                <input className="input" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              {showForm === 'groups' && (
                <div>
                  <label className="label">Description</label>
                  <textarea className="input" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
                </div>
              )}
              {showForm === 'sites' && (
                <>
                  <div>
                    <label className="label">Group</label>
                    <select className="input" value={form.groupId || ''} onChange={(e) => setForm({ ...form, groupId: e.target.value })}>
                      <option value="">— No group —</option>
                      {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Address</label>
                    <input className="input" value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Latitude</label>
                      <input className="input" type="number" step="any" value={form.latitude || ''} onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
                    </div>
                    <div>
                      <label className="label">Longitude</label>
                      <input className="input" type="number" step="any" value={form.longitude || ''} onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
                    </div>
                  </div>
                </>
              )}
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div className="flex gap-3 justify-end">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Excel Upload Results Modal */}
      {bulkResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Excel Upload Results</h3>
              <button onClick={() => setBulkResult(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
            </div>
            <div className="space-y-3">
              <div className="flex gap-4 text-sm flex-wrap">
                <span className="text-green-700 font-medium">{bulkResult.created} created</span>
                <span className="text-blue-700 font-medium">{bulkResult.updated || 0} updated</span>
                <span className="text-red-600 font-medium">{bulkResult.failed} failed</span>
                <span className="text-gray-400">{bulkResult.total} total row{bulkResult.total !== 1 ? 's' : ''}</span>
              </div>
              {bulkResult.failedRecords?.length > 0 && (
                <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Row</th>
                        <th className="text-left px-3 py-2">Identifier</th>
                        <th className="text-left px-3 py-2">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResult.failedRecords.map((f, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-3 py-1.5">{f.row}</td>
                          <td className="px-3 py-1.5">{f.identifier}</td>
                          <td className="px-3 py-1.5 text-red-600">{f.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
              <button className="btn-primary" onClick={() => setBulkResult(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={`Delete ${confirm?.type}`}
        message={`Delete "${confirm?.name}"? This cannot be undone.`}
        onConfirm={async () => {
          try {
            if (confirm.type === 'group') await groupApi.remove(confirm.id);
            else await siteApi.remove(confirm.id);
            setConfirm(null);
            load();
          } catch (err) {
            setConfirm(null);
            setDeleteBlockedMessage(err.response?.data?.message || err.response?.data?.error || 'Failed to delete — please try again.');
          }
        }}
        onCancel={() => setConfirm(null)}
      />

      {/* Shown when a delete attempt fails (e.g. the site/group still has
          associated data) — a plain confirm dialog has nowhere to surface
          that, so this is a dedicated popup with a Close button. */}
      {deleteBlockedMessage && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 p-6 w-full max-w-sm">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-amber-50 flex-shrink-0">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-amber-500">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="text-base font-bold text-slate-900 pt-2">Cannot Delete</h3>
            </div>
            <p className="text-sm text-gray-600 mb-5 leading-relaxed">{deleteBlockedMessage}</p>
            <div className="flex justify-end pt-3 border-t border-gray-100">
              <button className="btn-secondary" onClick={() => setDeleteBlockedMessage(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
